import { Button } from '@shared/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@shared/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { cn } from '@shared/utils/utils';
import { faCheck, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format, getDaysInMonth, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import * as React from 'react';

export interface ExpiryDatePickerLabels {
  day: string;
  month: string;
  year: string;
  preview?: string;
  search?: string;
  notFound?: string;
}

export interface ExpiryDatePickerProps {
  date?: Date;
  onDateChange?: (date: Date | undefined) => void;
  fromYear?: number;
  toYear?: number;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  id?: string;
  'data-testid'?: string;
  labels?: ExpiryDatePickerLabels;
}

interface DatePartOption {
  value: string;
  label: string;
}

interface SearchableDatePartSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: DatePartOption[];
  placeholder: string;
  searchPlaceholder: string;
  notFoundText: string;
  disabled?: boolean;
  error?: boolean;
  testId?: string;
  popoverClassName?: string;
}

const DEFAULT_LABELS: ExpiryDatePickerLabels = {
  day: 'Día',
  month: 'Mes',
  year: 'Año',
  preview: 'Fecha seleccionada',
  search: 'Buscar...',
  notFound: 'Sin resultados',
};

const MONTHS = Array.from({ length: 12 }, (_, index) => {
  const label = format(new Date(2024, index, 1), 'MMMM', { locale: es });
  return {
    value: String(index + 1),
    label: label.charAt(0).toUpperCase() + label.slice(1),
  };
});

const buildDate = (day: string, month: string, year: string): Date | undefined => {
  if (!day || !month || !year) return undefined;

  const candidate = new Date(Number(year), Number(month) - 1, Number(day));
  if (!isValid(candidate)) return undefined;
  if (candidate.getFullYear() !== Number(year)) return undefined;
  if (candidate.getMonth() !== Number(month) - 1) return undefined;
  if (candidate.getDate() !== Number(day)) return undefined;

  return candidate;
};

function SearchableDatePartSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  notFoundText,
  disabled = false,
  error = false,
  testId,
  popoverClassName,
}: SearchableDatePartSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const selectedOption = options.find((option) => option.value === value);

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
    setSearchValue('');
  };

  return (
    <Popover
      open={open && !disabled}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearchValue('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'w-full justify-between font-normal h-10 px-3 py-2 bg-card-bg border-card-border text-left',
            !value && 'text-muted-foreground',
            error && 'border-destructive focus-visible:ring-destructive',
          )}
        >
          <span className="truncate text-left">{selectedOption?.label ?? placeholder}</span>
          <FontAwesomeIcon icon={faChevronDown} className="ml-2 h-3 w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-auto p-0 min-w-[var(--radix-popover-trigger-width)]', popoverClassName)}
        align="start"
        sideOffset={4}
      >
        <Command shouldFilter>
          <CommandInput placeholder={searchPlaceholder} value={searchValue} onValueChange={setSearchValue} />
          <CommandList className="max-h-56">
            <CommandEmpty>{notFoundText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.value}`}
                  onSelect={() => handleSelect(option.value)}
                  className="cursor-pointer"
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    className={cn('mr-2 h-3.5 w-3.5 shrink-0', value === option.value ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ExpiryDatePicker({
  date,
  onDateChange,
  fromYear,
  toYear,
  disabled = false,
  className,
  error = false,
  id,
  'data-testid': testId,
  labels = DEFAULT_LABELS,
}: ExpiryDatePickerProps) {
  const currentYear = new Date().getFullYear();
  const yearFrom = fromYear ?? currentYear;
  const yearTo = toYear ?? currentYear + 20;
  const searchPlaceholder = labels.search ?? DEFAULT_LABELS.search!;
  const notFoundText = labels.notFound ?? DEFAULT_LABELS.notFound!;

  const [day, setDay] = React.useState('');
  const [month, setMonth] = React.useState('');
  const [year, setYear] = React.useState('');

  React.useEffect(() => {
    if (!date) {
      setDay('');
      setMonth('');
      setYear('');
      return;
    }

    setDay(String(date.getDate()));
    setMonth(String(date.getMonth() + 1));
    setYear(String(date.getFullYear()));
  }, [date]);

  const years = React.useMemo(() => {
    const list: number[] = [];
    for (let value = yearFrom; value <= yearTo; value += 1) {
      list.push(value);
    }
    return list;
  }, [yearFrom, yearTo]);

  const maxDays = React.useMemo(() => {
    if (!month || !year) return 31;
    return getDaysInMonth(new Date(Number(year), Number(month) - 1));
  }, [month, year]);

  const dayOptions = React.useMemo(
    () =>
      Array.from({ length: maxDays }, (_, index) => {
        const value = String(index + 1);
        return { value, label: value };
      }),
    [maxDays],
  );

  const yearOptions = React.useMemo(
    () => years.map((value) => ({ value: String(value), label: String(value) })),
    [years],
  );

  const updateDate = (nextDay: string, nextMonth: string, nextYear: string) => {
    onDateChange?.(buildDate(nextDay, nextMonth, nextYear));
  };

  const handleDayChange = (value: string) => {
    setDay(value);
    updateDate(value, month, year);
  };

  const handleMonthChange = (value: string) => {
    let nextDay = day;
    if (day && value && year) {
      const maxDay = getDaysInMonth(new Date(Number(year), Number(value) - 1));
      if (Number(day) > maxDay) {
        nextDay = String(maxDay);
        setDay(nextDay);
      }
    }
    setMonth(value);
    updateDate(nextDay, value, year);
  };

  const handleYearChange = (value: string) => {
    let nextDay = day;
    if (day && month && value) {
      const maxDay = getDaysInMonth(new Date(Number(value), Number(month) - 1));
      if (Number(day) > maxDay) {
        nextDay = String(maxDay);
        setDay(nextDay);
      }
    }
    setYear(value);
    updateDate(nextDay, month, value);
  };

  const selectedDate = buildDate(day, month, year);

  return (
    <div className={cn('space-y-2', className)} id={id} data-testid={testId}>
      <div className="grid grid-cols-1 sm:grid-cols-[5.5rem_minmax(0,1fr)_6.5rem] gap-3">
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">{labels.day}</span>
          <SearchableDatePartSelect
            value={day}
            onChange={handleDayChange}
            options={dayOptions}
            placeholder={labels.day}
            searchPlaceholder={searchPlaceholder}
            notFoundText={notFoundText}
            disabled={disabled}
            error={error}
            testId="expiry-date-day"
            popoverClassName="min-w-[11rem]"
          />
        </div>

        <div className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">{labels.month}</span>
          <SearchableDatePartSelect
            value={month}
            onChange={handleMonthChange}
            options={MONTHS}
            placeholder={labels.month}
            searchPlaceholder={searchPlaceholder}
            notFoundText={notFoundText}
            disabled={disabled}
            error={error}
            testId="expiry-date-month"
            popoverClassName="min-w-[14rem]"
          />
        </div>

        <div className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">{labels.year}</span>
          <SearchableDatePartSelect
            value={year}
            onChange={handleYearChange}
            options={yearOptions}
            placeholder={labels.year}
            searchPlaceholder={searchPlaceholder}
            notFoundText={notFoundText}
            disabled={disabled}
            error={error}
            testId="expiry-date-year"
            popoverClassName="min-w-[12rem]"
          />
        </div>
      </div>

      {selectedDate && (
        <p className="text-xs text-muted-foreground" data-testid="expiry-date-preview">
          {labels.preview}: {format(selectedDate, 'PPP', { locale: es })}
        </p>
      )}
    </div>
  );
}

ExpiryDatePicker.displayName = 'ExpiryDatePicker';

export { ExpiryDatePicker };
